import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Download, CheckCircle, Eye, EyeOff, Loader2, FileText, FileDown, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import WillCompletionVideoModal from '../WillCompletionVideoModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Step9Storage = ({ data, onPrevious, saving, subdomain, willId, useSubdomainRouting, isPartnerMode }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    place_of_signing: data.place_of_signing || '',
    date_of_signing: data.date_of_signing || new Date().toISOString().split('T')[0]
  });
  const [completing, setCompleting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [downloading, setDownloading] = useState({ pdf: false, docx: false });
  
  // Video modal state
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [completionVideo, setCompletionVideo] = useState(null);

  // Fetch completion video settings on mount
  useEffect(() => {
    const fetchCompletionVideo = async () => {
      try {
        const response = await axios.get(`${API}/videos/completion`);
        if (response.data.video) {
          setCompletionVideo(response.data.video);
        }
      } catch (error) {
        // Silent fail - video is optional
        console.log('No completion video configured');
      }
    };
    fetchCompletionVideo();
  }, []);

  // Helper function to get correct URL based on routing mode
  const getUrl = (path) => {
    if (useSubdomainRouting) return path;
    if (isEmbedded) return `/embed/${subdomain}${path}`;
    return path;
  };

  const inputClass = "bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20";
  const labelClass = "text-slate-300 font-medium text-sm";

  const getAuthHeader = () => {
    const token = localStorage.getItem(isPartnerMode ? 'partnerToken' : 'clientToken');
    return { Authorization: `Bearer ${token}` };
  };

  const apiPrefix = isPartnerMode ? 'partner' : 'client';

  // Fetch preview from backend
  const fetchPreview = async () => {
    if (!willId) return;
    
    setPreviewLoading(true);
    try {
      const response = await axios.get(
        `${API}/${apiPrefix}/will/${willId}/preview`,
        { headers: getAuthHeader() }
      );
      setPreviewContent(response.data);
    } catch (error) {
      console.error('Error fetching preview:', error);
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (showPreview && !previewContent) {
      fetchPreview();
    }
  }, [showPreview]);

  const handleComplete = async () => {
    if (!formData.place_of_signing) {
      toast.error('Please enter the place of signing');
      return;
    }

    setCompleting(true);
    try {
      // First save the final step data
      await axios.put(
        `${API}/${apiPrefix}/will/${willId}/step/9?advance=false`,
        formData,
        { headers: getAuthHeader() }
      );

      // Mark will as completed
      await axios.post(
        `${API}/${apiPrefix}/will/${willId}/complete`,
        {},
        { headers: getAuthHeader() }
      );

      toast.success('Your Will has been created successfully!');
      
      // Show video modal if configured, otherwise redirect to dashboard
      if (completionVideo && completionVideo.youtube_url) {
        setShowVideoModal(true);
      } else {
        if (isPartnerMode) {
          navigate('/partner/wills');
        } else {
          navigate(getUrl('/dashboard'));
        }
      }
    } catch (error) {
      console.error('Error completing will:', error);
      toast.error('Failed to complete Will. Please try again.');
    } finally {
      setCompleting(false);
    }
  };

  // Handle video modal close - redirect to dashboard
  const handleVideoModalClose = () => {
    setShowVideoModal(false);
    if (isPartnerMode) {
      navigate('/partner/wills');
    } else {
      navigate(getUrl('/dashboard'));
    }
  };

  const handleDownload = async (format) => {
    if (!willId) {
      toast.error('Please save your will first');
      return;
    }

    setDownloading(prev => ({ ...prev, [format]: true }));
    try {
      toast.info(`Generating ${format.toUpperCase()}...`);
      
      const response = await axios.post(
        `${API}/${apiPrefix}/will/${willId}/download?format=${format}`,
        {},
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const testatorName = data.testator_name?.replace(/\s+/g, '_') || 'Document';
      link.setAttribute('download', `Will_${testatorName}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`${format.toUpperCase()} downloaded successfully!`);
    } catch (error) {
      console.error('Error downloading:', error);
      if (error.response?.status === 404) {
        toast.error('Will not found. Please save your will first.');
      } else {
        toast.error(`Failed to download ${format.toUpperCase()}. Please try again.`);
      }
    } finally {
      setDownloading(prev => ({ ...prev, [format]: false }));
    }
  };

  // Format the preview text for display
  const formatPreviewText = () => {
    if (!previewContent?.will_text) return null;
    
    const text = previewContent.will_text;
    const familyMembers = previewContent.family_members || [];
    const lines = text.split('\n');
    
    return (
      <div className="will-preview font-['Arial',sans-serif] text-[11pt] leading-relaxed text-slate-200">
        {lines.map((line, idx) => {
          const trimmedLine = line.trim();
          
          // Empty line = spacer
          if (!trimmedLine) {
            return <div key={idx} className="h-3" />;
          }
          
          // Family table placeholder → render native table
          if (trimmedLine.includes('{{FAMILY_TABLE}}') || trimmedLine === '{{FAMILY_TABLE}}') {
            if (familyMembers.length === 0) return null;
            return (
              <div key={idx} className="my-4 flex justify-center">
                <table style={{ width: '80%', borderCollapse: 'collapse', fontSize: '11pt' }}>
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.08)' }}>
                      <th style={{ border: '1px solid #64748b', padding: '8px 12px', textAlign: 'center', fontWeight: 'bold', color: '#f1f5f9' }}>Sr. No.</th>
                      <th style={{ border: '1px solid #64748b', padding: '8px 12px', textAlign: 'left', fontWeight: 'bold', color: '#f1f5f9' }}>Name</th>
                      <th style={{ border: '1px solid #64748b', padding: '8px 12px', textAlign: 'left', fontWeight: 'bold', color: '#f1f5f9' }}>Relation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {familyMembers.map((m, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.03)' }}>
                        <td style={{ border: '1px solid #64748b', padding: '6px 12px', textAlign: 'center', color: '#cbd5e1' }}>{i + 1}</td>
                        <td style={{ border: '1px solid #64748b', padding: '6px 12px', color: '#cbd5e1' }}>{m.name}</td>
                        <td style={{ border: '1px solid #64748b', padding: '6px 12px', color: '#cbd5e1' }}>{m.relation}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }
          
          // Photo box placeholder → render native box
          if (trimmedLine.includes('{PHOTO_BOX}') || trimmedLine === '{PHOTO_BOX}') {
            return (
              <div key={idx} className="my-4 flex justify-center">
                <div style={{ width: '3in', height: '3in', border: '1px solid #64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ color: '#64748b', fontSize: '10pt', textAlign: 'center' }}>Affix Passport Size<br/>Photograph Here</span>
                </div>
              </div>
            );
          }
          
          // Title (WILL OF...)
          if (trimmedLine.startsWith('WILL OF')) {
            return (
              <h1 key={idx} className="text-center text-[16pt] font-bold mb-6 text-white">
                {trimmedLine}
              </h1>
            );
          }
          
          // Main numbered paragraphs (1. I state..., 2. I have..., 3. I declare...)
          // Split number and text, use flex layout for proper alignment
          const mainNumMatch = trimmedLine.match(/^([1-3]\.)\s+(.+)$/);
          if (mainNumMatch) {
            return (
              <div key={idx} className="flex mb-4 text-slate-300 text-justify">
                <span className="flex-shrink-0 w-16 pr-4">{mainNumMatch[1]}</span>
                <span className="flex-1">{mainNumMatch[2]}</span>
              </div>
            );
          }
          
          // Section headings (4. FAMILY DETAILS, 5. APPOINTMENT, etc.)
          if (/^\d+\.\s+[A-Z][A-Z\s]+$/.test(trimmedLine)) {
            return (
              <h2 key={idx} className="text-[12pt] font-bold mt-6 mb-3 text-white">
                {trimmedLine}
              </h2>
            );
          }
          
          // Sub-clauses (5.1, 6.1, 7.1.1, etc.) - use flex layout
          const clauseMatch = trimmedLine.match(/^(\d+\.\d+(?:\.\d+)?)\s+(.+)$/);
          if (clauseMatch) {
            return (
              <div key={idx} className="flex mb-2 text-slate-300 ml-6">
                <span className="flex-shrink-0 w-14">{clauseMatch[1]}</span>
                <span className="flex-1 text-justify">{clauseMatch[2]}</span>
              </div>
            );
          }
          
          // Parenthetical items (a), (b), etc.
          const parenMatch = trimmedLine.match(/^(\([a-z]\))\s*(.+)$/);
          if (parenMatch) {
            return (
              <div key={idx} className="flex mb-1 text-slate-300 ml-20">
                <span className="flex-shrink-0 w-10">{parenMatch[1]}</span>
                <span className="flex-1">{parenMatch[2]}</span>
              </div>
            );
          }
          
          // Indented text (starts with spaces in original)
          if (line.startsWith('    ')) {
            return (
              <p key={idx} className="ml-20 mb-2 text-slate-300">
                {trimmedLine}
              </p>
            );
          }
          
          // Regular text
          return (
            <p key={idx} className="mb-2 text-slate-300 text-justify">
              {trimmedLine}
            </p>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-8" data-testid="step9-form">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white border-b border-slate-700 pb-2">Declaration & Safe Storage</h3>
        
        {/* Signing Details */}
        <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700 space-y-4">
          <Label className="text-white font-medium">Signing Details</Label>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelClass}>Place of Signing *</Label>
              <Input
                type="text"
                value={formData.place_of_signing}
                onChange={(e) => setFormData(prev => ({ ...prev, place_of_signing: e.target.value }))}
                placeholder="e.g., Mumbai"
                required
                className={inputClass}
                data-testid="place-of-signing"
              />
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Date of Signing *</Label>
              <Input
                type="date"
                value={formData.date_of_signing}
                onChange={(e) => setFormData(prev => ({ ...prev, date_of_signing: e.target.value }))}
                required
                className={inputClass}
                data-testid="date-of-signing"
              />
            </div>
          </div>
        </div>

        {/* Will Preview */}
        <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
          <div className="flex justify-between items-center mb-4">
            <Label className="text-white font-medium flex items-center gap-2">
              <FileText size={18} className="text-amber-400" />
              Your Will Document
            </Label>
            <div className="flex gap-2">
              {showPreview && previewContent && (
                <Button
                  type="button"
                  onClick={() => {
                    setPreviewContent(null);
                    fetchPreview();
                  }}
                  variant="outline"
                  size="sm"
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  disabled={previewLoading}
                >
                  <RefreshCw size={16} className={`mr-2 ${previewLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              )}
              <Button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                variant="outline"
                size="sm"
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                {showPreview ? <EyeOff size={16} className="mr-2" /> : <Eye size={16} className="mr-2" />}
                {showPreview ? 'Hide Preview' : 'Show Preview'}
              </Button>
            </div>
          </div>
          
          {showPreview && (
            <div className="bg-slate-900 p-6 rounded-lg border border-slate-700 max-h-[600px] overflow-y-auto">
              {previewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin text-amber-400 mr-3" size={24} />
                  <span className="text-slate-400">Loading preview...</span>
                </div>
              ) : previewContent?.preview_html ? (
                <>
                  <div 
                    className="will-preview-html"
                    dangerouslySetInnerHTML={{ __html: previewContent.preview_html
                      .replace(/<html>|<\/html>|<head>.*?<\/head>/gs, '')
                      .replace(/<\/?body>/g, '')
                      .replace(/<!DOCTYPE html>/g, '')
                    }}
                    style={{
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '11pt',
                      lineHeight: '1.6',
                      color: '#e2e8f0',
                    }}
                  />
                  <style>{`
                    .will-preview-html div { color: #e2e8f0; }
                    .will-preview-html table { border-collapse: collapse; margin: 10px auto 20px; }
                    .will-preview-html th { color: #f1f5f9 !important; }
                    .will-preview-html td { color: #e2e8f0 !important; }
                  `}</style>
                </>
              ) : previewContent ? (
                formatPreviewText()
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <p>Preview not available. Save your will first to generate preview.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Download Options */}
        <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
          <Label className="text-white font-medium flex items-center gap-2 mb-4">
            <FileDown size={18} className="text-amber-400" />
            Download Your Will
          </Label>
          
          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              onClick={() => handleDownload('pdf')}
              variant="outline"
              className="border-red-600 text-red-400 hover:bg-red-500/10"
              disabled={downloading.pdf || !willId}
            >
              {downloading.pdf ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Download size={16} className="mr-2" />
              )}
              Download PDF
            </Button>
            <Button
              type="button"
              onClick={() => handleDownload('docx')}
              variant="outline"
              className="border-blue-600 text-blue-400 hover:bg-blue-500/10"
              disabled={downloading.docx || !willId}
            >
              {downloading.docx ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Download size={16} className="mr-2" />
              )}
              Download DOCX
            </Button>
          </div>
          
          {!willId && (
            <p className="text-amber-400 text-sm mt-3">
              Note: Please save your will using the button below before downloading.
            </p>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex flex-wrap justify-between gap-4 pt-6 border-t border-slate-700">
        <Button 
          type="button" 
          onClick={onPrevious} 
          variant="outline" 
          className="border-slate-600 text-slate-300 hover:bg-slate-800"
        >
          <ChevronLeft size={16} className="mr-2" /> Previous
        </Button>
        <Button 
          type="button"
          onClick={handleComplete}
          disabled={completing || !formData.place_of_signing}
          className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
        >
          {completing ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" /> Completing...
            </>
          ) : (
            <>
              <CheckCircle size={16} className="mr-2" /> Complete & Save Will
            </>
          )}
        </Button>
      </div>

      {/* Video Modal - shown after will completion */}
      <WillCompletionVideoModal
        isOpen={showVideoModal}
        onClose={handleVideoModalClose}
        videoUrl={completionVideo?.youtube_url}
        videoTitle={completionVideo?.video_title}
      />
    </div>
  );
};

export default Step9Storage;
